<script setup lang="ts">
import ArrowLeftIcon from '@/assets/double-arrow-left.svg'
import ArrowRightIcon from '@/assets/double-arrow-right.svg'
import OptionsMenu from '@/views/OptionsMenu.vue'
import EndRoundDrawer from '@/components/drawers/EndRoundDrawer.vue'
import Score from '@/views/Score.vue'
import HistoryVirtualScroller from '@/components/history/HistoryVirtualScroller.vue'
import TopBar from '@/components/topbar/TopBar.vue'
import Strategy from '@/components/Strategy.vue'
import Statistic from '@/views/Statistic.vue'
import SuggestedActions from '@/components/SuggestedActions.vue'
import CardsInHandMobile from '@/components/gameActions/CardsInHandMobile.vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { useSwipe } from '@vueuse/core/index.mjs'
import { Pagination } from 'swiper/modules'
import { ref } from 'vue'

import PokerTable from './components/table/PokerTable.vue'
import NLHSeat from './components/table/NLHSeat.vue'

import 'swiper/css/pagination'
import 'swiper/css'
import { watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { GameState, useGameState } from '@/stores/gameState'
import { getStrategy } from '@/api/getStrategy'
import { getHistory } from '@/api/getHistory'
import { getStatistics } from '@/api/getStatistics'
import { getMovesQuality } from '@/api/getMovesQuality'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import DotsLoader from '@/components/DotsLoader.vue'
import Typography from '@/components/ui/typography/Typography.vue'
import NoDataMessage from '@/components/NoDataMessage.vue'
import { useI18n } from 'vue-i18n'
import HintArrowBottom from '@/assets/hint-arrow-bottom.svg'
import HintArrowLeft from '@/assets/hint-arrow-left.svg'
import HintArrowRight from '@/assets/hint-arrow-right.svg'
import { loadPassedTraining, setPassedTraining } from '@/helpers/storage'
import WaitForYourTurn from '@/components/ui/WaitForYouTurn.vue'
import UiButton from '@/components/ui/button/Button.vue'

import { usePlayerSettings } from '@/stores/playerSettings'

// TODO: packages
type StatisticType = 'session' | 'total'

const modules = [Pagination]

const { t } = useI18n()
const router = useRouter()

const {
  gameState,
  roomState,
  player,
  roomId,
  isMyTurn,
  showButtons,
  isReviewMode,
  isHintMode,
  isHintMove,
  lastAction,
  suggestedActions,
  loadState
} = storeToRefs(useGameState())
const { sitDown } = useGameState()
const { nickname, avatarId } = storeToRefs(usePlayerSettings())

watch(loadState, (newValue) => {
  if (newValue === 'loaded') {
    sitDown({ name: nickname.value, avatarId: avatarId.value })
  }
})
const playerId = computed(() => player.value?.playerId ?? null)

const statsType = ref<StatisticType>('session')

watch(
  () => statsType.value,
  () => {
    if (player.value?.playerId) {
      updateStatistics()
    }
  }
)

const el = ref(null)
const isStrategyVisible = ref(false)
const isEndRoundVisible = ref(false)
const currentSlide = ref(1)
const { isSwiping, direction } = useSwipe(el)
const swiperRef = ref()
const showHint = ref(loadPassedTraining() !== '1')

const navigationLabels: { [key: number]: { left: string; right: string; current: string } } = {
  0: { left: '', right: 'Table', current: 'History' },
  1: { left: 'History', right: 'Stats', current: 'Table' },
  2: { left: 'Table', right: '', current: 'Stats' }
}

function handleSlideChange(i: number) {
  currentSlide.value = i
  isStrategyVisible.value = false
}

watch(
  () => isSwiping.value,
  (isSwiping) => {
    if (isSwiping && currentSlide.value === 1) {
      if (direction.value === 'down') {
        isStrategyVisible.value = true
      }
      if (direction.value === 'up') {
        isStrategyVisible.value = false
      }
    }
  }
)

watch(
  () => currentSlide.value,
  (slide) => {
    if (slide === 0) {
      updateHistory()
    }

    if (slide === 2) {
      updateStatistics()
    }
  }
)

const {
  fetchStatus: strategyDataFetchStatus,
  data: strategyData,
  error: strategyDataError,
  refetch: updateStrategy
} = useQuery({
  queryKey: [roomId, isMyTurn],
  enabled: isStrategyVisible,
  queryFn: async ({ queryKey }) => {
    if (!isStrategyVisible.value) {
      throw new Error('error.RequestSuspended')
    }
    if (!queryKey[1]) {
      throw new Error('error.WaitYourTurnToReceiveData')
    }
    if (!queryKey[0]) {
      throw new Error('error.NoRoomId')
    }
    const response = await getStrategy(queryKey[0] as string).catch((e) => {
      console.log(e)
      return new Error('error.FailedToReceiveDataFromAI')
    })
    if (response instanceof Error) {
      throw response
    }
    return response
  },
  retry: 1
})

const {
  isPending: isHistoryDataLoading,
  data: historyData,
  error: historyDataError,
  refetch: updateHistory
} = useQuery({
  queryKey: [playerId, 'nlh-history'],
  queryFn: async ({ queryKey }) => {
    if (!queryKey[0]) {
      throw new Error('error.NoPlayerId')
    }
    const response = await getHistory(queryKey[0]).catch((e) => {
      console.log(e)
      return new Error('error.FailedToFetchData')
    })
    if (response instanceof Error) {
      throw response
    }
    return response.items
  },
  retry: 1
})

const {
  isPending: isStatisticsDataLoading,
  data: statisticsData,
  error: statisticsDataError,
  refetch: updateStatistics
} = useQuery({
  queryKey: [playerId, statsType.value, isEndRoundVisible, 'nlh-statistics'],
  queryFn: async ({ queryKey }) => {
    if (!queryKey[0]) {
      throw new Error('error.NoPlayerId')
    }
    const sessionId =
      statsType.value === 'session' || isEndRoundVisible.value
        ? roomState.value.sessionId
        : undefined

    const response = await Promise.all([
      getStatistics(queryKey[0] as string, sessionId),
      getMovesQuality(queryKey[0] as string, sessionId)
    ]).catch((e) => {
      console.log(e)
      return new Error('error.FailedToFetchData')
    })

    if (response instanceof Error) {
      throw response
    }
    const [statistics, moves] = response
    return { moves, statistics }
  },
  retry: 1
})

watch(
  () => isStrategyVisible.value,
  async () => {
    updateStrategy()
  }
)

const onEndRound = () => {
  isEndRoundVisible.value = true
}

const handleExit = (args?: { close?: boolean }) => {
  onEndRound()
}
</script>

<template>
    <div
      v-if="showHint"
      @click="
        () => {
          showHint = false
          setPassedTraining()
        }
      "
      class="bg-10 fixed z-1000 top-0 bottom-0 left-0 right-0 flex flex-col px-[64px] py-[64px]"
    >
      <div class="grow flex flex-col items-center justify-center gap-[66px]">
        <div class="flex items-center justify-center gap-[22px]">
          <hint-arrow-bottom class="min-w-[32px]" />
          <div class="text-2 font-normal text-[18px] leading-[24px]">
            {{ t('NoLimitHoldem.label.SwipeDownToViewTheStrategyChart') }}
          </div>
        </div>
        <div class="flex items-center justify-center gap-[22px]">
          <hint-arrow-right class="min-w-[32px]" />
          <div class="text-2 font-normal text-[18px] leading-[24px]">
            {{ t('NoLimitHoldem.label.SwipeRightToOpenTheMovesAndHandHistory') }}
          </div>
        </div>
        <div class="flex items-center justify-center gap-[22px]">
          <hint-arrow-left class="min-w-[32px]" />
          <div class="text-2 font-normal text-[18px] leading-[24px]">
            {{ t('NoLimitHoldem.label.SwipeLeftToCheckStatisticsAndProgress') }}
          </div>
        </div>
      </div>
      <div class="text-[14px] leading-[20px] font-normal text-2 text-center">
        {{ t('NoLimitHoldem.label.TapTheScreenToCloseContinue') }}
      </div>
    </div>
    <div
      v-if="showHint"
      class="bg-8 blur-[100px] fixed z-1000 top-0 left-0 right-0 h-[10%] rounded-b-[350px]"
    />
    <div class="w-screen h-screen" ref="el">
      <div class="flex px-4 pt-10 items-center">
        <div class="text-white truncate">{{ roomState.name }}</div>
        <score class="mx-2" />
        <div class="grow" />
        <div class="flex items-center">
          <options-menu @exit="handleExit()" @exit-and-close="handleExit({ close: true })" />
        </div>
      </div>
      <div class="fixed z-999 bottom-[10px] left-4 right-4 flex items-center">
        <div
          class="flex gap-[3px] items-center"
          v-if="navigationLabels[currentSlide].left"
          @click="swiperRef.slidePrev()"
        >
          <arrow-left-icon class="w-[20px] h-[20px] [&>g>path]:stroke-secondary" />
          <Typography size="text-12-18" color="secondary">
            {{ navigationLabels[currentSlide].left }}
          </Typography>
        </div>
        <div class="grow" />
        <div
          class="flex gap-[3px] items-center"
          v-if="navigationLabels[currentSlide].right"
          @click="swiperRef.slideNext()"
        >
          <Typography size="text-12-18" color="secondary">
            {{ navigationLabels[currentSlide].right }}
          </Typography>
          <arrow-right-icon class="w-[20px] h-[20px] [&>g>path]:stroke-secondary" />
        </div>
      </div>
      <swiper
        @swiper="(swiper) => (swiperRef = swiper)"
        :initial-slide="1"
        :spaceBetween="50"
        :slidesPerView="1"
        :modules="modules"
        :pagination="true"
        @slideChange="(swiper) => handleSlideChange(swiper.activeIndex)"
        :style="{
          '--swiper-pagination-color': '#fff',
          '--swiper-pagination-bullet-inactive-color': '#666669'
        }"
        class="w-full h-[calc(100dvh-84px)] bg-background nlhe-mobile-swiper"
        id="nlhe-mobile-swiper"
        :data-slidename="navigationLabels[currentSlide].current"
      >
        <swiper-slide>
          <div class="flex flex-col h-full">
            <div class="flex justify-center" v-if="isHistoryDataLoading">
              <dots-loader size="large" />
            </div>
            <NoDataMessage v-else-if="historyData && !historyData.length">
              <template #img>
                <img src="@/assets/empty-hand-history.png" class="w-2/3 max-w-80" />
              </template>
              {{ $t('CustomDrills.placeholder.EmptyHistoryTitle') }}
              <template #message>{{ $t('CustomDrills.placeholder.EmptyHistoryText') }}</template>
            </NoDataMessage>
            <history-virtual-scroller
              v-else-if="historyData && historyData.length"
              :data="historyData"
              class="w-full h-full px-4 flex flex-col"
            />
            <div class="text-white flex justify-center" v-else-if="historyDataError">
              {{ t(historyDataError.message) }}
            </div>
            <div class="w-full h-10" />
          </div>
        </swiper-slide>
        <swiper-slide>
          <div class="flex flex-col items-center mt-2 w-full h-full">
            <top-bar
              :handHistory="roomState.handHistory"
              class="h-[76px] pl-[10px] mt-[-18px] pt-[15px] pb-[40px] pr-[5px] md:pt-[10px]"
            />
            <poker-table class="room__table">
              <template #table-seat="seatProps">
                <NLHSeat v-bind="seatProps" />
              </template>
            </poker-table>
            <transition appear name="room__animation-bottom-slide">
              <div
                class="relative z-10"
                v-if="
                  roomState.gameConfig?.learningMode && gameState === GameState.GAME && showButtons
                "
              >
                <suggested-actions
                  :actions="suggestedActions"
                  :lastActionNameRaw="lastAction?.actionNameRaw"
                  :isReviewMode="isReviewMode"
                  :isHintMode="isHintMode || isHintMove || isReviewMode"
                  @button-click="
                    async (action) => {
                      if (!isMyTurn) return
                      await action.fn()
                      lastAction = action
                    }
                  "
                />
              </div>
              <cards-in-hand-mobile
                v-else-if="!roomState.gameConfig?.learningMode && gameState === GameState.GAME"
                class="room__game-actions"
              />
            </transition>
          </div>
        </swiper-slide>
        <swiper-slide>
          <div class="flex flex-col h-full">
            <div class="flex justify-center" v-if="isHistoryDataLoading">
              <dots-loader size="large" />
            </div>
            <statistic
              v-else-if="statisticsData"
              :statistic="statisticsData.statistics"
              :movesQuality="statisticsData.moves.items"
              @update-statistic-type="(type: StatisticType) => (statsType = type)"
              :current-slide="currentSlide"
              :statisticType="statsType"
            />
            <div class="text-white flex justify-center" v-else-if="statisticsDataError">
              {{ t(statisticsDataError.message) }}
            </div>
            <div class="w-full h-10" />
          </div>
        </swiper-slide>
      </swiper>
      <transition name="strategy--mobile__animation-top-slide">
        <div class="strategy--mobile" v-if="isStrategyVisible">
          <div
            v-if="strategyDataFetchStatus === 'fetching'"
            class="h-[calc(100vh/2)] flex flex-col justify-center items-center"
          >
            <dots-loader size="large" />
          </div>
          <strategy v-else-if="strategyData" :strategy-data="strategyData" />
          <div
            v-else-if="
              strategyDataError && strategyDataError.message !== 'error.WaitYourTurnToReceiveData'
            "
            class="text-white h-[calc(100vh/2)] flex flex-col justify-center items-center"
          >
            {{ t(strategyDataError.message) }}
          </div>
          <div
            v-else-if="
              strategyDataError && strategyDataError.message === 'error.WaitYourTurnToReceiveData'
            "
            class="h-[calc(100vh/2)] flex flex-col justify-center items-center"
          >
            <wait-for-your-turn />
          </div>
        </div>
      </transition>
    </div>
    <end-round-drawer
      v-if="statisticsData"
      :open="isEndRoundVisible"
      :moves="statisticsData.moves.items"
      :statistic-info="statisticsData.statistics"
      @cancel="isEndRoundVisible = false"
    >
      <template #button-primary>
        <ui-button @click="router.push('/games')" class="w-full" variant="default2">{{
          $t('EndSession.button.NextPractice')
        }}</ui-button>
      </template>
      <template #button-secondary>
        <ui-button @click="router.push('/games')" class="w-full" variant="ghostOutlined">{{
          $t('EndSession.button.Exit')
        }}</ui-button>
      </template>
    </end-round-drawer>
</template>

<style>
.strategy--mobile__animation-top-slide-enter-active,
.strategy--mobile__animation-top-slide-leave-active {
  transition: all 0.5s ease;
}

.strategy--mobile__animation-top-slide-enter-from,
.strategy--mobile__animation-top-slide-leave-to {
  opacity: 0;
  transform: translate(-50%, -150%) !important;
}

.strategy--mobile {
  position: fixed;
  z-index: 99999;
  left: 50%;
  top: 45%;
  transform: translate(-50%, -50%);
  background: #1f1f24;
  border-radius: 8px;
  padding: 16px;
  width: 358px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.nlhe-mobile-swiper .swiper-pagination {
  position: fixed;
}
</style>
